import { OrgOption } from './types'
import { OrganizationItem } from './OrganizationItem'
import { OrganizationGroup } from './OrganizationGroup'
import { DividerLine } from './DividerLine'
import { useOrganizationHierarchy } from './hooks/useOrganizationHierarchy'

interface OrganizationHierarchyViewProps {
  options: OrgOption[]
  selectedOptions: OrgOption[]
  onChange: (option: OrgOption | OrgOption[]) => void
}

export function OrganizationHierarchyView({
  options,
  selectedOptions,
  onChange
}: OrganizationHierarchyViewProps) {
  const {
    hierarchy,
    hierarchyInView,
    brokerageOptions,
    teamOptions,
    officesWithoutBrokerage,
    officeOptions,
    hasBrokerage,
    hasTeams,
    defaultOption,
    setHierarchy,
    setHierarchyInView
  } = useOrganizationHierarchy(options)

  const handleOptionChecked = (option: OrgOption) => {
    onChange(option)
  }

  const handleHierarchyClick = (branch: OrgOption, index: number) => {
    if (index === 0) {
      setHierarchy([defaultOption])
      setHierarchyInView(defaultOption)
    } else {
      setHierarchy((prev) => prev.slice(0, index + 1))
      setHierarchyInView(branch)
    }
  }

  const handleOrgClick = (org: OrgOption) => {
    const { type, id } = org.value
    const branchId = id.split('-')[1]

    const hasTeams = options.some(
      (option) =>
        option.value.type === 'Team' &&
        option?.value?.primaryOfficeId === branchId
    )

    if (type === 'Office' && !hasTeams) return

    setHierarchy((prev) => [...prev, org])
    setHierarchyInView(org)
  }

  return (
    <div className="flex max-h-[196px] flex-col overflow-y-auto py-2">
      {hierarchy.map((branch, index) => (
        <div
          key={branch.value.id}
          className={`relative ${index > 0 ? 'mt-4' : ''}`}>
          <OrganizationItem
            item={branch}
            truncateLabel={false}
            isActive={hierarchyInView.value.id === branch.value.id}
            isChecked={selectedOptions.some(
              (s) => s.value.id === branch.value.id
            )}
            onClick={() => handleHierarchyClick(branch, index)}
            onCheckChange={() => handleOptionChecked(branch)}
          />
          {index < hierarchy.length - 1 && <DividerLine position="below" />}
        </div>
      ))}
      {!hasTeams && (
        <>
          {!hasBrokerage && brokerageOptions?.length > 0 && (
            <OrganizationGroup
              withDivider
              title="Brokerages"
              items={brokerageOptions}
              selectedOptions={selectedOptions}
              onOptionClick={handleOrgClick}
              onOptionCheck={handleOptionChecked}
            />
          )}
          {!hasBrokerage && officesWithoutBrokerage?.length > 0 && (
            <OrganizationGroup
              withDivider
              title="Offices"
              items={officesWithoutBrokerage}
              selectedOptions={selectedOptions}
              onOptionClick={handleOrgClick}
              onOptionCheck={handleOptionChecked}
            />
          )}
          {hasBrokerage && officeOptions?.length > 0 && (
            <OrganizationGroup
              withDivider
              title="Offices"
              items={officeOptions}
              selectedOptions={selectedOptions}
              onOptionClick={handleOrgClick}
              onOptionCheck={handleOptionChecked}
            />
          )}
        </>
      )}
      {hasTeams && (
        <OrganizationGroup
          withDivider
          title="Teams"
          items={teamOptions}
          selectedOptions={selectedOptions}
          onOptionCheck={handleOptionChecked}
        />
      )}
    </div>
  )
}
